import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { streamReportDownload, type ReportParams } from '@/api/reports';
import { RESPONSE_CODES } from '@/consts';
import { t } from '@/i18n';
import { triggerXlsxDownload } from '@/utils/download';
import { showError } from './globalErrorSlice';

export type AppNotification = {
  message: string;
  severity: 'error' | 'warning' | 'success';
};

type ReportDownloadState = {
  isDownloading: boolean;
  notification: AppNotification | null;
  // Whether the query-timeout dialog should be shown. Kept in Redux (not
  // component-local state) because the SSE connection outlives the Report
  // page component across navigation — see isDownloading/notification above
  // and hooks.ts for the same reasoning.
  queryTimeoutDialogOpen: boolean;
};

// Timeouts for the reports SSE stream (/api/v1/reports/stream). Layered so
// each guard fires before the next — must stay consistent with the backend
// (atss-api src/constants/const.ts):
//  1. Backend REPORT_QUERY_MAX_EXECUTION_TIME_MS (110s) — MySQL kills the
//     query itself.
//  2. Backend SSE_TIMEOUT_MS (115s) — server safety net; sends
//     { queryTimeout: true } and closes the stream.
//  3. This SSE_TIMEOUT_MS (115s) — client fallback for when even the
//     server's own timeout message is lost in transit (dropped connection,
//     server crash) and no message ever arrives. Kept below the ALB's idle
//     timeout so the user gets an actionable "narrow your search" dialog
//     instead of the connection dying with no explanation.
//  4. ALB idle_timeout_seconds = 120s is the hard ceiling everything above
//     must fire before.
const SSE_TIMEOUT_MS = 115 * 1000;

// reportDownloadSlice より前に定義することで extraReducers から参照できる。
// thunk 本体内の reportDownloadSlice.actions 参照はクロージャで遅延評価されるため、
// モジュール読み込み完了後に dispatch が呼ばれる時点では初期化済みになる。
export const downloadReport = createAsyncThunk(
  'reportDownload/download',
  async ({ from, to, basis }: ReportParams, { dispatch }) => {
    const { setNotification, openQueryTimeoutDialog } = reportDownloadSlice.actions;

    return new Promise<void>((resolve, reject) => {
      // onmessage と onerror が両方発火しても二重 settle しないよう制御する。
      // es.close() 後に onerror が発火するブラウザ実装への防御。
      let settled = false;

      const es = streamReportDownload(
        { from, to, basis },
        (event: MessageEvent) => {
          if (settled) return;
          try {
            const data = JSON.parse(event.data as string) as {
              base64?: string;
              filename?: string;
              empty?: boolean;
              queryTimeout?: boolean;
              code?: string;
            };
            if (data.code === RESPONSE_CODES.UNAUTHORIZED) {
              window.location.href = `/auth/login?returnTo=${encodeURIComponent(
                window.location.pathname + window.location.search,
              )}`;
              resolve();
              return;
            }
            if (data.queryTimeout) {
              dispatch(openQueryTimeoutDialog());
              resolve();
              return;
            }
            if (data.empty) {
              dispatch(
                setNotification({ message: t('dashboard_report_no_data'), severity: 'warning' }),
              );
              resolve();
              return;
            }
            if (!data.base64) throw new Error('base64 is missing in response');
            triggerXlsxDownload(data.base64, data.filename);
            dispatch(
              setNotification({
                message: t('dashboard_report_download_complete'),
                severity: 'success',
              }),
            );
            resolve();
          } catch (e) {
            console.error('Failed to parse SSE message:', e);
            dispatch(
              setNotification({
                message: t('dashboard_report_download_error'),
                severity: 'error',
              }),
            );
            reject(e instanceof Error ? e : new Error(String(e)));
          } finally {
            settled = true;
            clearTimeout(timeoutId);
            es.close();
          }
        },
        () => {
          if (settled) return;
          settled = true;
          clearTimeout(timeoutId);
          es.close();
          console.error('SSE connection error');
          // 接続エラーはサーバー障害・ネットワーク障害の可能性が高いため、
          // toast ではなくリロードボタン付きのグローバルエラーダイアログを表示する。
          // セッション切れは code: UNAUTHORIZED の SSE メッセージとして送られるため
          // onmessage 側で処理済み — ここに来るのは実際の接続エラーのみ。
          dispatch(showError({ message: t('dashboard_report_download_error'), statusCode: null }));
          reject(new Error('SSE connection error'));
        },
      );

      // es のコールバック内で clearTimeout を呼ぶため es の後に定義。
      // コールバックは非同期実行なので、timeoutId が未初期化のまま参照される問題はない。
      const timeoutId = setTimeout(() => {
        if (settled) return;
        settled = true;
        es.close();
        dispatch(openQueryTimeoutDialog());
        reject(new Error('SSE timeout'));
      }, SSE_TIMEOUT_MS);
    });
  },
);

const initialState: ReportDownloadState = {
  isDownloading: false,
  notification: null,
  queryTimeoutDialogOpen: false,
};

const reportDownloadSlice = createSlice({
  name: 'reportDownload',
  initialState,
  reducers: {
    setNotification: (state, action: PayloadAction<AppNotification>) => {
      state.notification = action.payload;
    },
    clearNotification: (state) => {
      state.notification = null;
    },
    openQueryTimeoutDialog: (state) => {
      state.queryTimeoutDialogOpen = true;
    },
    closeQueryTimeoutDialog: (state) => {
      state.queryTimeoutDialogOpen = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(downloadReport.pending, (state) => {
        state.isDownloading = true;
        state.notification = null;
        state.queryTimeoutDialogOpen = false;
      })
      .addCase(downloadReport.fulfilled, (state) => {
        state.isDownloading = false;
      })
      .addCase(downloadReport.rejected, (state) => {
        state.isDownloading = false;
      });
  },
});

export const {
  setNotification,
  clearNotification,
  openQueryTimeoutDialog,
  closeQueryTimeoutDialog,
} = reportDownloadSlice.actions;

export default reportDownloadSlice.reducer;
